// Phase 29.6.1 · SR Dashboard — SE self view + manager reportee view.
//
// Mobile-first for SE, denser for manager. Data from GET /api/sr-dashboard.
// Focus-SKU list is cross-referenced against 29.4 admin focus list on the
// backend; empty focus_sku_achievement → "No focus SKUs configured" state.

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Target, TrendingUp, Users, Package, Flame,
         AlertTriangle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";

const INR = new Intl.NumberFormat("en-IN",
  { style: "currency", currency: "INR", maximumFractionDigits: 0 });

export default function SrDashboard() {
  const [params, setParams] = useSearchParams();
  const user_id = params.get("user_id") || "";
  const period = params.get("period") || "monthly";
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/sr-dashboard", { params: {
      ...(user_id ? { user_id } : {}),
      period,
    } })
      .then((r) => setD(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [user_id, period]);

  const changePeriod = (p) => {
    const next = new URLSearchParams(params);
    next.set("period", p);
    setParams(next);
  };

  if (loading && !d) return (
    <div className="p-8 text-center text-neutral-400">
      <Loader2 className="h-6 w-6 inline animate-spin" />
    </div>
  );
  if (!d) return <div className="p-6 text-neutral-500">No data.</div>;

  return (
    <div className="p-4 pb-32 space-y-4" data-testid="sr-dash-root">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-400">
          SR Dashboard · {d.target_user.role_code}
        </div>
        <h1 className="text-2xl font-extrabold text-neutral-900">
          {d.target_user.name || "—"}
        </h1>
        <div className="mt-1 flex gap-2">
          {["monthly", "3m"].map((p) => (
            <button
              key={p}
              onClick={() => changePeriod(p)}
              data-testid={`sr-dash-period-${p}`}
              className={`text-xs px-3 py-1 rounded-full border ${
                period === p
                  ? "bg-neutral-900 text-white border-neutral-900"
                  : "border-neutral-300 text-neutral-600"
              }`}
            >{p === "monthly" ? "Last 30 days" : "Last 3 months"}</button>
          ))}
        </div>
      </div>

      {/* Avg sales */}
      <Card className="border-neutral-200 rounded-2xl"
            data-testid="sr-dash-avg-sales">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-neutral-500 text-[11px] uppercase tracking-widest">
            <TrendingUp className="h-3.5 w-3.5" />
            Average sales · trailing 3 months
          </div>
          <div className="mt-2 flex items-baseline gap-4">
            <div className="text-2xl font-extrabold">
              {INR.format(d.avg_sales_last_3_months.value_inr)}
            </div>
            <div className="text-xs text-neutral-500 font-mono">
              {d.avg_sales_last_3_months.qty_kg.toFixed(1)} kg/mo
            </div>
          </div>
          <div className="text-[11px] text-neutral-400 mt-1">
            Total {INR.format(d.avg_sales_last_3_months.value_inr_total)} ·
            {" "}{d.avg_sales_last_3_months.qty_kg_total.toFixed(1)} kg
          </div>
        </CardContent>
      </Card>

      {/* Beat target */}
      <Card className="border-neutral-200 rounded-2xl"
            data-testid="sr-dash-beat-target">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-neutral-500 text-[11px] uppercase tracking-widest">
            <Target className="h-3.5 w-3.5" />
            Beat target · {d.beat_target.period_key}
          </div>
          <div className="mt-2 flex items-baseline gap-4">
            <div className="text-2xl font-extrabold">
              {d.beat_target.achievement_pct != null
                ? `${d.beat_target.achievement_pct.toFixed(1)}%`
                : "—"}
            </div>
            <div className="text-xs text-neutral-500 font-mono">
              {INR.format(d.beat_target.achieved_value)} /
              {" "}{INR.format(d.beat_target.target_value)}
            </div>
          </div>
          {d.beat_target.target_value === 0 && (
            <div className="text-[11px] text-amber-700 mt-1">
              No target set for {d.beat_target.period_key}.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Outlets — total + productive */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="border-neutral-200 rounded-2xl"
              data-testid="sr-dash-outlets-total">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-neutral-500 text-[11px] uppercase tracking-widest">
              <Users className="h-3.5 w-3.5" />
              Total outlets
            </div>
            <div className="text-2xl font-extrabold mt-2">
              {d.total_outlets}
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 rounded-2xl bg-emerald-50/40"
              data-testid="sr-dash-outlets-productive">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-emerald-700 text-[11px] uppercase tracking-widest">
              <Sparkles className="h-3.5 w-3.5" />
              Productive · trailing {d.settings.productive_window_days}d
            </div>
            <div className="text-2xl font-extrabold mt-2">
              {d.productive_outlets}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Focus SKUs (live from 29.4 admin list) */}
      <Card className="border-neutral-200 rounded-2xl"
            data-testid="sr-dash-focus-sku-list">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-neutral-500 text-[11px] uppercase tracking-widest">
            <Flame className="h-3.5 w-3.5" />
            Focus SKU achievement
          </div>
          {d.focus_sku_achievement.length === 0 ? (
            <div className="mt-3 text-xs text-neutral-400"
                 data-testid="sr-dash-focus-empty">
              No focus SKUs configured. Ask an admin to set them under
              Focus SKUs.
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              {d.focus_sku_achievement.map((f) => (
                <div key={f.sku_id}
                     className="flex items-baseline justify-between text-sm"
                     data-testid="sr-dash-focus-sku-row">
                  <div className="min-w-0 flex-1 truncate">
                    <span className="font-mono text-[11px] text-neutral-400">
                      {f.sku_code}
                    </span>
                    <span className="ml-2 text-neutral-800">{f.sku_name}</span>
                  </div>
                  <div className="text-neutral-900 font-semibold whitespace-nowrap">
                    {INR.format(f.value_inr)}
                    <span className="text-[11px] text-neutral-500 ml-2 font-normal">
                      {f.qty_kg.toFixed(1)}kg
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top-selling SKU — always rendered so testid is stable */}
      <Card className="border-neutral-200 rounded-2xl"
            data-testid="sr-dash-top-sku">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-neutral-500 text-[11px] uppercase tracking-widest">
            <Package className="h-3.5 w-3.5" />
            Top-selling SKU · this period
          </div>
          {d.top_selling_sku ? (
            <>
              <div className="mt-2 font-mono text-[11px] text-neutral-400">
                {d.top_selling_sku.sku_code}
              </div>
              <div className="text-neutral-900 font-bold">
                {d.top_selling_sku.sku_name}
              </div>
              <div className="text-xs text-neutral-500 mt-1">
                {INR.format(d.top_selling_sku.value_inr)} ·{" "}
                {d.top_selling_sku.qty_kg.toFixed(1)} kg
              </div>
            </>
          ) : (
            <div className="mt-2 text-xs text-neutral-500"
                 data-testid="sr-dash-top-sku-empty">
              No sales yet in this period — go book that first order!
            </div>
          )}
        </CardContent>
      </Card>

      {/* New outlets */}
      <Card className="border-neutral-200 rounded-2xl"
            data-testid="sr-dash-new-outlets">
        <CardContent className="p-4">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500">
            New outlets this month
          </div>
          <div className="mt-1 text-2xl font-extrabold">
            {d.new_outlets_this_month.length}
          </div>
          {d.new_outlets_this_month.slice(0, 5).map((o) => (
            <div key={o.id} className="text-xs text-neutral-600 truncate">
              · {o.name}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Zero-billed */}
      <Card className="border-neutral-200 rounded-2xl"
            data-testid="sr-dash-zero-billed-list">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-neutral-500 text-[11px] uppercase tracking-widest">
            <AlertTriangle className="h-3.5 w-3.5" />
            Zero-billed · trailing {d.settings.zero_billed_days}d
          </div>
          <div className="mt-1 text-2xl font-extrabold">
            {d.zero_billed_90d_outlet_ids.length}
          </div>
          {d.lost_outlets_90d.slice(0, 5).map((o) => (
            <div key={o.id} className="text-xs text-neutral-600 truncate">
              · {o.code} {o.name}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Lost outlets — same underlying set, kept as a distinct testid for hooks */}
      <Card className="border-neutral-200 rounded-2xl"
            data-testid="sr-dash-lost-outlets">
        <CardContent className="p-4">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500">
            Lost outlets (no order 90+ days)
          </div>
          <div className="mt-1 text-2xl font-extrabold">
            {d.lost_outlets_90d.length}
          </div>
        </CardContent>
      </Card>

      {/* Low productivity */}
      <Card className="border-neutral-200 rounded-2xl"
            data-testid="sr-dash-low-productivity">
        <CardContent className="p-4">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500">
            Low productivity ·
            {" "}&lt; {d.settings.low_productivity_min_orders_per_month}/mo
          </div>
          <div className="mt-1 text-2xl font-extrabold">
            {d.low_productivity_outlets.length}
          </div>
          {d.low_productivity_outlets.slice(0, 5).map((o) => (
            <div key={o.id} className="text-xs text-neutral-600 truncate">
              · {o.name} — {o.orders_in_period} orders
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
